import { useMemo, useState } from 'react'
import type { PipelineStage } from 'types/entity'

/**
 * Port of `funcToBindDealProgress`: stages are grouped by pipeline name, the fill
 * percentage comes from the last completed stage's position, and pipelines past
 * the first sit behind a more../less.. toggle.
 */
export function DealPipeline({ stages }: { stages: PipelineStage[] }) {
  const [showAll, setShowAll] = useState(false)

  const groups = useMemo(() => groupByPipeline(stages), [stages])

  if (groups.length === 0) {
    return <div className="empty-state">No pipeline attached to this lead.</div>
  }

  const [first, ...rest] = groups

  return (
    <div className="progress-section">
      <PipelineGroup group={first} />

      {rest.length > 0 && (
        <>
          <button type="button" className="link-btn" onClick={() => setShowAll((v) => !v)}>{showAll ? 'less..' : 'more..'}</button>
          {showAll && rest.map((group) => <PipelineGroup key={group.name} group={group} />)}
        </>
      )}
    </div>
  )
}

interface PipelineGroupData {
  name: string
  stages: PipelineStage[]
  fillPercent: number
}

/** A stage counts as complete when `PipeLineStage` is non-empty. */
function groupByPipeline(stages: PipelineStage[]): PipelineGroupData[] {
  const map = new Map<string, PipelineStage[]>()
  for (const stage of stages) {
    const name = (stage.PinelineName ?? '').trim()
    const list = map.get(name)
    if (list) list.push(stage)
    else map.set(name, [stage])
  }

  return Array.from(map.entries()).map(([name, groupStages]) => {
    let lastCompleteIndex = -1
    groupStages.forEach((stage, i) => {
      if (stage.PipeLineStage !== '' && stage.PipeLineStage != null) lastCompleteIndex = i
    })
    const denominator = groupStages.length - 1
    const fillPercent =
      lastCompleteIndex <= 0 || denominator <= 0 ? 0 : (lastCompleteIndex / denominator) * 100

    return { name, stages: groupStages, fillPercent }
  })
}

function PipelineGroup({ group }: { group: PipelineGroupData }) {
  return (
    <div className="progress-section-inner">
      <h4>{group.name}</h4>
      <div className="progress-cont">
        <div className="progress-div">
          <div className="bar">
            <div className="bar__fill" style={{ width: `${group.fillPercent}%` }} />
          </div>

          {group.stages.map((stage, i) => {
            const complete = stage.PipeLineStage !== '' && stage.PipeLineStage != null
            return (
              <div key={`${stage.StageName}-${i}`} className={`point${complete ? ' point--complete' : ''}`}>
                {complete ? <TickIcon /> : <div className="bullet" />}
                <span className="label-text">
                  {stage.StageName} ({stage.Probability}%)
                  {complete && stage.CreatedDate && (
                    <small>
                      {stage.CreatedDate}
                      {stage.User_Login ? ` (${stage.User_Login})` : ''}
                    </small>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function TickIcon() {
  return (
    <span className="stage-tick">
      <svg viewBox="0 0 37 37" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path
          className="path"
          d="M30.5,6.5L30.5,6.5c6.6,6.6,6.6,17.4,0,24l0,0c-6.6,6.6-17.4,6.6-24,0l0,0c-6.6-6.6-6.6-17.4,0-24l0,0C13.1-0.2,23.9-0.2,30.5,6.5z"
        />
        <polyline className="path" points="11.6,20 15.9,24.2 26.4,13.8" />
      </svg>
    </span>
  )
}
